import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getAccommodation, deleteAccommodation } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  Home,
  MapPin,
  BedDouble,
  Bath,
  DollarSign,
} from "lucide-react";

export default function AccommodationPage() {
  const { data: user } = useAuth();
  const { getLocationIdsForQuery, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteAccommodationFn = useAction(deleteAccommodation);

  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<
    "newest" | "oldest" | "price_low" | "price_high"
  >("newest");

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("cards");

  // Fetch items
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getAccommodation, {
    locationIds: getLocationIdsForQuery(),
    filters: {},
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item: any) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.metadata?.address
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Type filter
    if (selectedType !== "all") {
      filtered = filtered.filter(
        (item: any) => item.metadata?.type === selectedType,
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "price_low":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (a.metadata?.price || 0) - (b.metadata?.price || 0),
        );
        break;
      case "price_high":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.price || 0) - (a.metadata?.price || 0),
        );
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedType, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm("Are you sure you want to delete this listing?")) {
      try {
        await deleteAccommodationFn({ itemId });
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "APARTMENT": return "bg-blue-100 text-blue-800";
      case "HOUSE": return "bg-green-100 text-green-800";
      case "ROOM": return "bg-purple-100 text-purple-800";
      case "SHARED": return "bg-orange-100 text-orange-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Accommodation</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Find housing in {selectedLocation?.name || "Location"} (
            {filteredItems.length} listings)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <input
                    type="text"
                    placeholder="Search properties..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="APARTMENT">Apartment</option>
                    <option value="HOUSE">House</option>
                    <option value="ROOM">Room</option>
                    <option value="SHARED">Shared</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                  >
                    <option value="newest">Newest</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>

                <Button className="w-full" onClick={() => setIsFilterExpanded(false)}>Apply</Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (selectedLocation?.locationCode) {
                  navigate(`/locations/${selectedLocation.locationCode}/accommodation/add`);
                } else {
                  navigate("/accommodation/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline">List Property</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers for Table View */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-4 min-w-0 truncate">Property</div>
            <div className="col-span-2 min-w-0 truncate">Type</div>
            <div className="col-span-2 min-w-0 truncate">Price</div>
            <div className="col-span-2 min-w-0 truncate">Location</div>
            {user?.isAdmin && <div className="col-span-2 text-right">Actions</div>}
          </div>
        )}
        
        {/* Filter Row */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search properties..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="col-span-2">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Types</option>
              <option value="APARTMENT">Apartment</option>
              <option value="HOUSE">House</option>
              <option value="ROOM">Room</option>
              <option value="SHARED">Shared</option>
            </select>
          </div>
          <div className="col-span-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12 text-red-500">
              Error loading properties. Please try again.
            </div>
          )}

          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">No properties found.</div>
          )}

          {!isLoading && !error && filteredItems.length > 0 && (
            <div className={cn(
              viewMode === "table" ? "w-full" : "p-6",
              viewMode === "cards" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"
            )}>
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    viewMode === "cards" 
                      ? enhancedPageLayoutStyles.card.container 
                      : viewMode === "list" 
                        ? "bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                        : "px-6 py-4 hover:bg-sidebar-hover transition-colors border-b border-gray-100 cursor-pointer"
                  )}
                  onClick={() => navigate(`/accommodation/${item.id}`)}
                >
                  {viewMode === "table" ? (
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <div className="font-medium text-gray-900 truncate">{item.title}</div>
                        <div className="text-xs text-gray-500 truncate">{item.metadata?.address}</div>
                      </div>
                      <div className="col-span-2">
                        <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getTypeColor(item.metadata?.type))}>
                          {item.metadata?.type}
                        </span>
                      </div>
                      <div className="col-span-2 font-bold text-success">
                        ${item.metadata?.price}/{item.metadata?.period?.charAt(0)}
                      </div>
                      <div className="col-span-2 text-sm text-gray-500 truncate">{item.metadata?.address}</div>
                      {user?.isAdmin && (
                        <div className="col-span-2 flex justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                          <EnhancedIconButton onClick={() => navigate(`/accommodation/${item.id}/edit`)} icon={Edit} title="Edit" />
                          <EnhancedIconButton onClick={() => handleDelete(item.id)} icon={Trash2} title="Delete" variant="danger" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={enhancedPageLayoutStyles.card.content}>
                      <div className={enhancedPageLayoutStyles.card.header}>
                        <span className={cn("px-2 py-1 text-xs font-medium rounded", getTypeColor(item.metadata?.type))}>
                          {item.metadata?.type}
                        </span>
                        <div className="text-lg font-bold text-success">
                          ${item.metadata?.price}<span className="text-sm font-normal text-gray-500">/{item.metadata?.period?.toLowerCase()}</span>
                        </div>
                      </div>
                      <h3 className={enhancedPageLayoutStyles.card.title}>{item.title}</h3>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                        <div className="flex items-center gap-1"><BedDouble size={14}/>{item.metadata?.bedrooms}</div>
                        <div className="flex items-center gap-1"><Bath size={14}/>{item.metadata?.bathrooms}</div>
                      </div>
                      <p className={enhancedPageLayoutStyles.card.description}>{item.description}</p>
                      <div className={enhancedPageLayoutStyles.card.footer}>
                        <div className={enhancedPageLayoutStyles.card.meta}>by {item.author.name || item.author.email}</div>
                        {user?.isAdmin && (
                          <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                            <EnhancedIconButton onClick={() => navigate(`/accommodation/${item.id}/edit`)} icon={Edit} title="Edit" />
                            <EnhancedIconButton onClick={() => handleDelete(item.id)} icon={Trash2} title="Delete" variant="danger" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
